const SESSION_KEYS = {
  userToken: 'userToken',
  user: 'user',
  impersonationToken: 'impersonationToken',
  entraClaims: 'entraClaims',
} as const;

export type SessionToken = string | null;

/**
 * SessionUser mirrors AccountInfoResponse — the shape stored in sessionStorage `user`.
 * Subset of AccountInfoResponse; only fields accessed by React code are listed explicitly.
 * UsableAmount / ChargeBalance are POS-computed fields that may be patched in at login.
 */
export interface SessionUser {
  // Identity
  UserId?: string | number;
  LinkToken?: string;
  FirstName?: string;
  LastName?: string;
  EmailAddress?: string;
  // Plan / carrier — LakerGroupNumber is the member-facing "Plan Name"
  LakerGroupNumber?: string;
  LakerCarrierCode?: string;
  // Laker member identity — required by the accumulator and copay endpoints.
  // LakerSubscriberNum is a composite value; legacy code splits it on '*' when
  // only the subscriber portion is needed (see navbarController.js).
  LakerSubscriberNum?: string;
  LakerPersonCode?: string;
  // Status flags
  StatusCode?: string;
  IsPOSUser?: boolean;
  IsLoggedOn?: boolean;
  // POS / billing
  UsableAmount?: number;
  ChargeBalance?: number;
  CreditCardBalance?: number;
  ChargeAccountMaxCredit?: number;
  CanAddCreditCard?: boolean;
  CanChangeCreditCard?: boolean;
  CanUseCreditCard?: boolean;
  HasCreditCardOnFile?: boolean;
  CreditCardExplanation?: string;
  CreditCardNumber?: string;
  CreditCardExpMonth?: number;
  CreditCardExpYear?: number;
  // Addresses
  StreetAddresses?: SessionAddress[];
  ShippingAddressTypeId?: number;
  // Mail order enrollment
  IsInGuardian?: boolean;
  IsMailOrderActivationPending?: boolean;
  // Dependants / features
  Dependants?: Array<Record<string, unknown>>;
  Features?: Array<{
    FeatureId: number;
    FeatureClass?: string;
    FeatureName?: string;
    FeatureUrl?: string;
    IsTile?: boolean;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

/** Address shape as stored in SessionUser.StreetAddresses (matches AccountInfo.StreetAddresses) */
export interface SessionAddress {
  AddressTypeId?: number;
  AddressTypeDescr?: string;
  City?: string;
  IsRequired?: boolean;
  State?: string;
  Street1?: string;
  Street2?: string;
  Zip?: string;
}

export interface EntraClaims {
  logonToken?: string;
  firstName?: string;
  subscriberId?: string;
  rxGroupNumber?: string;
  dob?: string;
  [key: string]: unknown;
}

function readStorage(key: string): string | null {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  return window.sessionStorage.getItem(key);
}

function writeStorage(key: string, value: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  window.sessionStorage.setItem(key, value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseJson<T>(value: string | null, guard?: (parsed: unknown) => parsed is T): T | null {
  if (!value) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(value);
    if (guard && !guard(parsed)) {
      return null;
    }
    return parsed as T;
  } catch {
    return null;
  }
}

export function isSessionUser(value: unknown): value is SessionUser {
  return isRecord(value);
}

export function isEntraClaims(value: unknown): value is EntraClaims {
  return isRecord(value);
}

export function getUserToken(): SessionToken {
  return readStorage(SESSION_KEYS.userToken);
}

export function getImpersonationToken(): SessionToken {
  return readStorage(SESSION_KEYS.impersonationToken);
}

/**
 * True when this session is an advocate impersonation session.
 *
 * Mirrors legacy userService.GetIsImpersonating() (www/app/services/userService.js):
 * impersonation is signalled by the mere PRESENCE of impersonationToken.
 *
 * Do NOT reintroduce an `impersonationToken !== userToken` comparison here. Legacy
 * featureController.startImpersonationSession() performs a pseudo-login that writes the
 * same token to BOTH `userToken` and `impersonationToken`, so the two are equal for the
 * whole impersonation session by design. Comparing them is therefore always false in
 * production, which silently disables every read-only guard that depends on this.
 *
 * The URL fallback also mirrors legacy: on the initial /impersonate/:token load the
 * token has not been written to sessionStorage yet.
 */
export function getIsImpersonating(): boolean {
  const impersonationToken = getImpersonationToken();

  if (impersonationToken === null) {
    return isImpersonationUrl();
  }

  return impersonationToken.length > 0;
}

function isImpersonationUrl(): boolean {
  if (typeof document === 'undefined' || !document.location) {
    return false;
  }

  return document.location.href.indexOf('/impersonate/') > -1;
}

export function getSessionUser<T = SessionUser>(
  guard?: (parsed: unknown) => parsed is T
): T | null {
  return parseJson<T>(
    readStorage(SESSION_KEYS.user),
    guard || (isSessionUser as (parsed: unknown) => parsed is T)
  );
}

export function getEntraClaims<T = EntraClaims>(
  guard?: (parsed: unknown) => parsed is T
): T | null {
  return parseJson<T>(
    readStorage(SESSION_KEYS.entraClaims),
    guard || (isEntraClaims as (parsed: unknown) => parsed is T)
  );
}

export function setSessionUser(user: SessionUser | Record<string, unknown>): void {
  writeStorage(SESSION_KEYS.user, JSON.stringify(user));
}

export function clearSession(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  Object.values(SESSION_KEYS).forEach(key => {
    window.sessionStorage.removeItem(key);
  });
}
